import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listScans, type AdminScanSummary } from '../lib/api';
import { theme } from '../theme';

export function ScansPage() {
  const [scans, setScans] = useState<AdminScanSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [makeFilter, setMakeFilter] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState<'' | 'success' | 'failure'>('');

  useEffect(() => {
    setLoading(true);
    listScans({
      limit: 50,
      make: makeFilter || undefined,
      outcome: outcomeFilter || undefined,
    })
      .then((res) => {
        setScans(res.scans);
        setNextCursor(res.nextCursor);
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [makeFilter, outcomeFilter]);

  const loadMore = async () => {
    if (!nextCursor) return;
    const res = await listScans({
      limit: 50,
      cursor: nextCursor,
      make: makeFilter || undefined,
      outcome: outcomeFilter || undefined,
    });
    setScans((prev) => [...prev, ...res.scans]);
    setNextCursor(res.nextCursor);
  };

  return (
    <div>
      <h1>Recent Scans</h1>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <input
          placeholder="Filter by make…"
          value={makeFilter}
          onChange={(e) => setMakeFilter(e.target.value)}
        />
        <select
          value={outcomeFilter}
          onChange={(e) => setOutcomeFilter(e.target.value as '' | 'success' | 'failure')}
        >
          <option value="">All outcomes</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
        </select>
      </div>
      {error && <p style={{ color: theme.status.error }}>Error: {error}</p>}
      {loading && <p>Loading…</p>}
      {!loading && scans.length === 0 && <p>No scans match this filter.</p>}
      {!loading && scans.length > 0 && (
        <>
          <ScanTable scans={scans} />
          {nextCursor && (
            <button
              onClick={loadMore}
              style={{
                marginTop: 16,
                padding: '8px 16px',
                background: theme.surface.panel,
                border: `1px solid ${theme.surface.borderStrong}`,
                color: theme.text.primary,
                borderRadius: theme.radius.md,
              }}
            >
              Load more
            </button>
          )}
        </>
      )}
    </div>
  );
}

function ScanTable({ scans }: { scans: AdminScanSummary[] }) {
  return (
    <table style={{ width: '100%' }}>
      <thead>
        <tr style={{ background: theme.surface.header }}>
          <Th>Scanned</Th>
          <Th>ID</Th>
          <Th>Dealer</Th>
          <Th>Vehicle</Th>
          <Th>VIN</Th>
          <Th>Outcome</Th>
          <Th>Status</Th>
          <Th>DTCs</Th>
          <Th>Readings</Th>
          <Th>Actions</Th>
        </tr>
      </thead>
      <tbody>
        {scans.map((s) => (
          <tr key={s.scanId} style={{ borderBottom: `1px solid ${theme.surface.border}` }}>
            <Td>
              <time>{formatRelative(s.scannedAt)}</time>
            </Td>
            <Td style={{ fontFamily: theme.fonts.mono, fontSize: 12 }}>
              {s.scanShortcodeBody ? `OB-${s.scanShortcodeBody}` : `${s.scanId.slice(0, 8)}…`}
            </Td>
            <Td>
              <DealerCell summary={s} />
            </Td>
            <Td>
              {s.make ?? '—'} {s.model ?? ''} {s.modelYear ?? ''}
            </Td>
            <Td style={{ fontFamily: theme.fonts.mono, fontSize: 12 }}>{s.vin ?? '—'}</Td>
            <Td>
              <Badge variant={s.outcome === 'success' ? 'ok' : 'fail'}>{s.outcome}</Badge>
            </Td>
            <Td>{s.overallStatus ?? '—'}</Td>
            <Td>{s.totalDtcs ?? '—'}</Td>
            <Td style={{ fontFamily: theme.fonts.mono, fontSize: 12 }}>
              <ReadingsRatio stats={s.signalSetStats} />
            </Td>
            <Td>
              <Link to={`/scans/${s.scanId}`}>Details</Link>
              {s.reportUrl && (
                <>
                  {' · '}
                  <a href={s.reportUrl} target="_blank" rel="noreferrer">
                    PDF
                  </a>
                </>
              )}
              {s.batteryReportUrl && (
                <>
                  {' · '}
                  <a href={s.batteryReportUrl} target="_blank" rel="noreferrer">
                    Battery PDF
                  </a>
                </>
              )}
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ReadingsRatio({ stats }: { stats: AdminScanSummary['signalSetStats'] }) {
  if (!stats) return <span style={{ color: theme.text.tertiary }}>—</span>;
  // "readings / total commands" reads at-a-glance: how many of the planned
  // OBD commands actually returned a usable signal value. succeededCommands
  // is between readings and totalCommands (a single command can produce
  // multiple readings or zero on NRC), so readings is the dealer-facing
  // metric and total is the denominator.
  return (
    <span>
      {stats.readingsCount} / {stats.totalCommands}
    </span>
  );
}

function DealerCell({ summary }: { summary: AdminScanSummary }) {
  if (!summary.userId) {
    return <span style={{ color: theme.text.tertiary }}>—</span>;
  }
  const name = [summary.firstName, summary.lastName].filter(Boolean).join(' ').trim();
  return (
    <Link to={`/app-users/${encodeURIComponent(summary.userId)}`}>{name || 'View dealer'}</Link>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '8px 12px',
        fontSize: 12,
        color: theme.text.secondary,
        fontWeight: 600,
      }}
    >
      {children}
    </th>
  );
}
function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '8px 12px', fontSize: 13, ...style }}>{children}</td>;
}
function Badge({ children, variant }: { children: React.ReactNode; variant: 'ok' | 'fail' }) {
  const color = variant === 'ok' ? theme.status.success : theme.status.error;
  return <span style={{ color, fontWeight: 600 }}>{children}</span>;
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}
